import { ref } from 'vue'
import { useAuth } from '@/composables/useAuth'
import { useHostServices } from '@/composables/format-edit/useHostServices'
import { useAiStreaming } from '@/composables/yagiprime/useAiStreaming'
import { useAiMyprDialogControls } from '@/composables/format-edit/useAiMyprDialogControls'
import { emitter } from '@/plugins/emitter'
import type { ShokurekishoFormatData } from '@/composables/format-edit/useShokurekishoFormatData'

type AiAxiosVm = {
  $axios: {
    post: (
      url: string,
      data: FormData
    ) => Promise<{
      data: { success?: boolean; usage_count?: number }
    }>
  }
}

/**
 * 診断結果ページから引き継いだ診断ID。
 * これを送らないとサーバは uuid の最新診断を使うため、他人のシェアURLを開いていた場合に
 * 画面に出ていた結果と AI に渡る結果がずれる。
 */
function yagidigramExamId(): string | null {
  return new URLSearchParams(window.location.search).get('examresult')
}

type GlobalVarWithPayments = {
  isYagiPrime: boolean
  in_app_payments?: boolean
}

export function useShokurekishoAiMypr(formatData: ShokurekishoFormatData) {
  const { vm, varuna, site, deviceLib, globalVar, yagiprimePaymentService } =
    useHostServices()
  const host = vm as unknown as AiAxiosVm
  const { user, isAuthenticated } = useAuth()
  const { aiMyprVisible, openAiMyprDialog, closeAiMyprDialog } =
    useAiMyprDialogControls()

  /**
   * ヤギディ診断を材料に使うか。既定 ON（未診断ならサーバー側で何も足されないだけ）。
   * 診断文はクライアントに持ち回らせず、サーバーが uuid / 診断ID から引く（varuna: ai_introduce）。
   */
  const useDigram = ref(true)
  // 書式編集はロード後にクエリを消すため、生成時に読むと空になる。setup 時点で取っておく
  const digramExamId = yagidigramExamId()
  const aiResultView = ref(false)
  const aiResultMyprResult = ref('')
  const aiUsageCount = ref(0)
  const aiMaxUsageCount = ref(1)

  const {
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    startAIStream,
    toggleAiReasoning,
    cancelAIStream
  } = useAiStreaming()

  const gVar = globalVar as typeof globalVar & GlobalVarWithPayments

  function fetchAiUsageCount() {
    if (!isAuthenticated.value || !user.value?.uid) {
      return Promise.resolve()
    }
    const body = new FormData()
    body.append('uuid', user.value.uid)
    // 消費されるのは ai_introduce 側の 'introduce' 行（'pr' は ai_introduce_pr の行）
    body.append('category_type', 'introduce')
    return host.$axios
      .post(varuna.endPoints.getAiUsageCount, body)
      .then((response) => {
        if (response.data.success) {
          aiUsageCount.value = response.data.usage_count || 0
          aiMaxUsageCount.value = gVar.isYagiPrime ? 10 : 1
        }
      })
  }

  function showAiMyprDialog() {
    fetchAiUsageCount().finally(() => {
      openAiMyprDialog()
    })
  }

  function closeDialog() {
    cancelAIStream()
    closeAiMyprDialog()
  }

  function getAIMypr() {
    if (!gVar.isYagiPrime && aiUsageCount.value >= 1) {
      if (formatData.prMessage) {
        aiResultMyprResult.value = formatData.prMessage.replace(/\n/g, '<br>')
        aiResultView.value = true
        return
      }
    }
    const body = new FormData()
    if (isAuthenticated.value && user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
    body.append('education', '')
    body.append('strength', String(formatData.ai_mypr_good || ''))
    body.append('weakness', String(formatData.ai_mypr_bad || ''))
    body.append('hobby', String(formatData.ai_mypr_hobby || ''))
    body.append('future', String(formatData.ai_mypr_wanted || ''))
    body.append('free', String(formatData.ai_mypr_appeal || ''))
    if (useDigram.value) {
      body.append('use_digram', '1')
      if (digramExamId) {
        body.append('digram_random_id', digramExamId)
      }
    }
    body.append('app_flag', site.appFlag)
    body.append('device_flg', deviceLib.isMobileDevice ? 'SP' : 'PC')
    body.append('version', '3')
    body.append('site_flag', 'r')

    startAIStream(varuna.endPoints.aiIntroduce, body, {
      resultRef: aiResultMyprResult,
      viewRef: aiResultView,
      onError: (error) => {
        const message = error.message || '失敗しました'
        if (message === 'AIの使用回数が上限に達しました。') {
          emitter.emit(
            'show-yagiprime-payment-dialog',
            'self-introduction-shokumu'
          )
        }
      }
    })
  }

  function getAIMyprContinue() {
    const body = new FormData()
    if (isAuthenticated.value && user.value?.uid) {
      body.append('uuid', user.value.uid)
    }
    body.append(
      'data',
      '作成文章:自己PR\n' +
        '学歴:' +
        (formatData.lastestEdu || '') +
        '\n' +
        '職歴:' +
        (formatData.lastestJob || '') +
        '\n' +
        '資格:' +
        ((formatData.licenseHistories &&
          formatData.licenseHistories.map((l) => l.description).join(',')) ||
          '') +
        '\n' +
        '得意なこと:' +
        (formatData.ai_mypr_good || '') +
        '\n' +
        '苦手なこと:' +
        (formatData.ai_mypr_bad || '') +
        '\n' +
        '趣味:' +
        (formatData.ai_mypr_hobby || '') +
        '\n' +
        '今後やりたいこと:' +
        (formatData.ai_mypr_wanted || '') +
        '\n' +
        'その他アピールしたいこと:' +
        (formatData.ai_mypr_appeal || '') +
        '\n' +
        '文章:' +
        aiResultMyprResult.value.replace(/<br>/gi, '\n')
    )

    startAIStream(varuna.endPoints.aiSentenseContinue, body, {
      resultRef: aiResultMyprResult,
      viewRef: aiResultView
    })
  }

  function goYagiprimeRegister() {
    yagiprimePaymentService.useFeature(vm, 'self-introduction-shokumu', {
      onSuccess: () => {
        getAIMyprContinue()
      },
      onCancel: () => {}
    })
  }

  function applyMyprResult() {
    if (aiIsStreaming.value) return
    formatData.prMessage = aiResultMyprResult.value.replace(/<br>/gi, '\n')
    closeDialog()
    aiResultView.value = false
  }

  return {
    aiMyprVisible,
    useDigram,
    aiResultView,
    aiResultMyprResult,
    aiUsageCount,
    aiMaxUsageCount,
    aiIsStreaming,
    aiIsThinking,
    aiThinkingLabel,
    aiReasoningText,
    aiShowReasoning,
    showAiMyprDialog,
    closeDialog,
    getAIMypr,
    goYagiprimeRegister,
    applyMyprResult,
    toggleAiReasoning,
    cancelAIStream,
    inAppPayments: () => !!gVar.in_app_payments,
    isYagiPrime: () => !!gVar.isYagiPrime
  }
}
